import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { useAuth } from '../context/AuthContext';
import { getOrgTimezone, getCurrentPayPeriod, dateTimeLocalToISO, formatDateInTz } from '../utils/timezone';
import organizationService from '../services/organization';
import userService from '../services/user';
import timesheetService from '../services/timesheet';
import timesheetApprovalService from '../services/timesheetApproval';
import { toNumOr } from '@/utils/numeric';

/** localStorage key prefix — stores the period end date we last notified about. */
const REMINDED_KEY_PREFIX = 'timesheet_reminder_notified_';

/** Stable local-notification ID reserved for the next scheduled reminder. */
const SCHEDULED_NOTIF_ID = 42001;

/** Stable local-notification ID used when firing an immediate (past-due) reminder. */
const IMMEDIATE_NOTIF_ID = 42002;

/** Delay (ms) before firing an immediate notification — gives the OS time to register it. */
const IMMEDIATE_NOTIFICATION_DELAY_MS = 1000;

/**
 * Returns the UTC Date that represents 9 AM on `dateStr` in `orgTimezone`.
 * dateStr must be a YYYY-MM-DD string.
 */
function get9AmUtc(dateStr: string, orgTimezone: string): Date {
  return new Date(dateTimeLocalToISO(`${dateStr}T09:00`, orgTimezone));
}

/**
 * Given the current pay period's start date and the pay frequency, returns
 * the start and end dates of the immediately-preceding pay period.
 */
function getPreviousPayPeriod(
  currentStartDate: string,
  payFrequency: 'weekly' | 'fortnightly',
): { startDate: string; endDate: string } {
  const periodDays = payFrequency === 'weekly' ? 7 : 14;
  const currentStart = new Date(currentStartDate + 'T00:00:00Z');

  const prevEnd = new Date(currentStart);
  prevEnd.setUTCDate(currentStart.getUTCDate() - 1);

  const prevStart = new Date(prevEnd);
  prevStart.setUTCDate(prevEnd.getUTCDate() - (periodDays - 1));

  return {
    startDate: prevStart.toISOString().slice(0, 10),
    endDate: prevEnd.toISOString().slice(0, 10),
  };
}

/**
 * Core async check — loads settings, evaluates reminder conditions and either
 * schedules a future local notification or fires one immediately.
 */
async function runReminderCheck(orgTimezone: string): Promise<void> {
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');

    // ── 1. Ensure the notification channel exists (Android 8+) ────────────
    if (Capacitor.getPlatform() === 'android') {
      await LocalNotifications.createChannel({
        id: 'timesheet-reminders',
        name: 'Timesheet Reminders',
        description: 'Reminds you to submit your timesheet at the end of each pay period',
        importance: 4, // IMPORTANCE_HIGH
        visibility: 1, // VISIBILITY_PUBLIC
        vibration: true,
        sound: 'default',
      });
    }

    // ── 2. Check / request notification permission ─────────────────────────
    const permStatus = await LocalNotifications.checkPermissions();
    if (permStatus.display !== 'granted') {
      const requested = await LocalNotifications.requestPermissions();
      if (requested.display !== 'granted') return;
    }

    // ── 3. Load org pay-cycle settings and the user's pay frequency ────────
    const [orgRes, profileRes] = await Promise.all([
      organizationService.getCurrentOrganization(),
      userService.getMyProfile(),
    ]);

    if (!orgRes.success || !orgRes.data) return;
    if (!profileRes.success || !profileRes.data) return;

    const payCycleStartDay = orgRes.data.pay_cycle_start_day ?? 4;
    const fortnightlyRefDate = orgRes.data.fortnightly_cycle_reference_date
      ? orgRes.data.fortnightly_cycle_reference_date.slice(0, 10)
      : null;

    // No default — if pay_frequency isn't set on the profile, we cannot reliably
    // decide which day is a period boundary, so skip the reminder entirely
    // rather than firing weekly reminders at a fortnightly user (or vice-versa).
    const rawPayFreq = profileRes.data.pay_frequency;
    if (rawPayFreq !== 'weekly' && rawPayFreq !== 'fortnightly') {
      console.warn('[TimesheetReminder] pay_frequency missing on profile — skipping reminder check');
      return;
    }
    const payFrequency: 'weekly' | 'fortnightly' = rawPayFreq;

    // ── 4. Compute current and previous pay periods ────────────────────────
    const currentPeriod = getCurrentPayPeriod(
      payCycleStartDay,
      payFrequency,
      orgTimezone,
      fortnightlyRefDate,
    );

    const prevPeriod = getPreviousPayPeriod(currentPeriod.startDate, payFrequency);

    // ── 5. (Re-)schedule the notification for the END of the current period ─
    //   Fires at 9 AM on the day after the current period ends.
    const nextNotifyDateStr = (() => {
      const d = new Date(currentPeriod.endDate + 'T00:00:00Z');
      d.setUTCDate(d.getUTCDate() + 1);
      return d.toISOString().slice(0, 10);
    })();
    const nextNotifyAt = get9AmUtc(nextNotifyDateStr, orgTimezone);

    if (nextNotifyAt > new Date()) {
      // Cancel any stale scheduled notification then reschedule.
      await LocalNotifications.cancel({ notifications: [{ id: SCHEDULED_NOTIF_ID }] });
      await LocalNotifications.schedule({
        notifications: [
          {
            id: SCHEDULED_NOTIF_ID,
            title: 'Timesheet Submission Due',
            body: `Your ${payFrequency === 'fortnightly' ? 'fortnightly' : 'weekly'} pay period has ended — please submit your timesheet for approval.`,
            schedule: { at: nextNotifyAt, allowWhileIdle: true },
            channelId: 'timesheet-reminders',
            extra: { type: 'timesheet_reminder' },
          },
        ],
      });
    }

    // ── 6. Check whether we should fire an immediate reminder for the ──────
    //   previous period (i.e. app opened after the 9 AM deadline).

    // The reminder for the previous period is due at 9 AM on the first day of the
    // current period (which is the day immediately after the previous period ended).
    const prevPeriodReminderDueAt = get9AmUtc(currentPeriod.startDate, orgTimezone);
    if (new Date() < prevPeriodReminderDueAt) return; // Too early — nothing to do now.

    // Have we already sent a reminder for this period?
    const storageKey = `${REMINDED_KEY_PREFIX}${prevPeriod.endDate}`;
    if (localStorage.getItem(storageKey)) return;

    // Fetch summary (hours worked) and existing submissions for the previous period.
    const [summaryRes, submissionsRes] = await Promise.all([
      timesheetService.getSummary({
        startDate: prevPeriod.startDate,
        endDate: prevPeriod.endDate,
      }),
      timesheetApprovalService.getSubmissions(),
    ]);

    if (!summaryRes.success || !summaryRes.data) return;

    const totalHours = toNumOr(summaryRes.data.summary.total_hours);
    if (totalHours <= 0) {
      // No time recorded — mark as seen so we do not check again this period.
      localStorage.setItem(storageKey, 'seen');
      return;
    }

    // Is there already a pending or approved submission covering the previous period?
    const submissions = submissionsRes.success ? (submissionsRes.data ?? []) : [];
    const hasSubmission = submissions.some(
      s =>
        s.start_date <= prevPeriod.endDate &&
        s.end_date >= prevPeriod.startDate &&
        (s.status === 'pending' || s.status === 'approved'),
    );

    if (hasSubmission) {
      localStorage.setItem(storageKey, 'seen');
      return;
    }

    // ── 7. Fire the immediate reminder ────────────────────────────────────
    const hoursStr = totalHours.toFixed(1);
    const startLabel = formatDateInTz(prevPeriod.startDate, orgTimezone);
    const endLabel = formatDateInTz(prevPeriod.endDate, orgTimezone);

    await LocalNotifications.schedule({
      notifications: [
        {
          id: IMMEDIATE_NOTIF_ID,
          title: 'Timesheet Submission Due',
          body: `You have ${hoursStr} hours worked (${startLabel} – ${endLabel}) that haven't been submitted for approval.`,
          schedule: { at: new Date(Date.now() + IMMEDIATE_NOTIFICATION_DELAY_MS), allowWhileIdle: true },
          channelId: 'timesheet-reminders',
          extra: { type: 'timesheet_reminder' },
        },
      ],
    });

    localStorage.setItem(storageKey, 'notified');
  } catch (err) {
    console.error('[TimesheetReminder] check failed:', err);
  }
}

/**
 * Schedules and fires local timesheet-submission reminder notifications.
 *
 * - At 9 AM the morning after each pay period ends, checks whether the user
 *   has unsubmitted hours and — if so — delivers a local push notification.
 * - Re-evaluates whenever the app returns to the foreground.
 * - Runs only on native Capacitor platforms (Android / iOS).
 */
export function useTimesheetReminder(): void {
  const { isAuthenticated, user } = useAuth();
  const orgTimezone = getOrgTimezone(user);

  useEffect(() => {
    if (!isAuthenticated || !Capacitor.isNativePlatform()) return;

    // Run immediately on mount.
    runReminderCheck(orgTimezone);

    // Re-run every time the app comes back to the foreground.
    let listenerHandle: { remove: () => void } | undefined;

    CapApp.addListener('appStateChange', state => {
      if (state.isActive) {
        runReminderCheck(orgTimezone);
      }
    }).then(handle => {
      listenerHandle = handle;
    });

    return () => {
      listenerHandle?.remove();
    };
  }, [isAuthenticated, orgTimezone]);
}
